import React, { useState, useEffect } from "react";
import { Modal, Box, Typography, TextField, Button } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";

const DriverAproveReject = ({
  isOpen,
  onClose,
  title,
  message = "",
  billStatus,
  onYesClick,
  newStatus,
  onRejectClick,
  customClass = "",
  loading,
}) => {
  const [remark, setRemark] = useState("");
  const [error, setError] = useState("");
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [visibleStatus, setVisibleStatus] = useState("");

  const adminInfo = localStorage.getItem('adminInfo')
  ? JSON.parse(localStorage.getItem('adminInfo'))
  : null;
  const userRole = adminInfo?.profile;
  // console.log("userrole", userRole);
  
  useEffect(() => {
    if (billStatus) {
      setVisibleStatus(billStatus);

      const timeout = setTimeout(() => {
        setVisibleStatus("");
      }, 1000); // hide after 1 second

      return () => clearTimeout(timeout);
    }
  }, [billStatus]);

  useEffect(() => {
    if (!isOpen) {
      setRemark("");
      setError("");
    }
  }, [isOpen]);

  const handleReject = () => {
    setIsRejectModalOpen(true);
  };

  const handleApprove = () => {
    setError("");
    onYesClick();
  };

  const handleRejectSubmit = () => {
    if (!remark.trim()) {
      setError("Remark is required!");
      return;
    }
    setError("");
    onRejectClick(remark);
    setIsRejectModalOpen(false);
  };

  const handleRejectClose = () => {
    setIsRejectModalOpen(false);
  };

  return (
    <div>
      {/* Main Modal */}
      <Modal
        open={isOpen}
        onClose={onClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            width: "400px",
            borderRadius: "8px",
          }}
        >
          {/* Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
            <Typography variant="h6" id="modal-title" sx={{ textAlign: "left" }}>
              {title}
            </Typography>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-800 transition duration-200"
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
              aria-label="Close"
            >
              ✕
            </button>
          </Box>

          {/* Message */}
          <Box sx={{ textAlign: "center", marginBottom: 2 }}>
            {message && <Typography sx={{ color: "text.secondary" }}>{message}</Typography>}
          </Box>

          {billStatus === "loss" && (
            <p className="text-red-500 mt-2 text-center">Cannot approve, the bill amount is a loss.</p>
          )}

          {/* Actions and Bill Status */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "30px" }}>
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              <>
                <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                  <Button
                    variant="text"
                    onClick={handleReject}
                    disabled={loading}
                    sx={{
                      color: "red",
                      marginRight: 2,
                    }}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="text"
                    onClick={handleApprove}
                    sx={{
                      color: "green",
                      marginRight: 2,
                    }}
                    // disabled={billStatus === "loss" || loading}
                    disabled={billStatus === 'loss' && userRole !== 'Admin'}
                  >
                    Approve
                  </Button>
                </Box>

                {/* Bill Status (1 second visibility) */}
                {visibleStatus && (
                  <p
                    className={`mt-2 font-semibold ${
                      visibleStatus === "loss"
                        ? "text-red-500"
                        : visibleStatus === "low profit"
                        ? "text-orange-500"
                        : visibleStatus === "mid profit"
                        ? "text-yellow-500"
                        : visibleStatus === "good profit"
                        ? "text-green-600"
                        : "text-gray-500"
                    }`}
                  >
                    {visibleStatus === "loss"
                      ? "LOSS"
                      : visibleStatus === "low profit"
                      ? "LOW PROFIT"
                      : visibleStatus === "mid profit"
                      ? "MID PROFIT"
                      : visibleStatus === "good profit"
                      ? "GOOD PROFIT"
                      : visibleStatus.toUpperCase()}
                  </p>
                )}
              </>
            )}
          </Box>
        </Box>
      </Modal>

      {/* Reject Modal */}
      <Modal
        open={isRejectModalOpen}
        onClose={handleRejectClose}
        aria-labelledby="reject-modal-title"
        aria-describedby="reject-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            width: "400px",
            borderRadius: "8px",
          }}
        >
          {/* Reject Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
            <Typography variant="h6" id="reject-modal-title" sx={{ textAlign: "left" }}>
              Tell us the reason
            </Typography>
            <button
              onClick={handleRejectClose}
              className="text-gray-500 hover:text-gray-800 transition duration-200"
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
              aria-label="Close"
            >
              ✕
            </button>
          </Box>

          {/* Text Input */}
          <Box sx={{ textAlign: "center", marginBottom: 2 }}>
            <TextField
              value={remark}
              inputProps={{ maxLength: 200 }}
              onChange={(e) => setRemark(e.target.value)}
              label="Reason for reject"
              fullWidth
              variant="outlined"
              error={Boolean(error)}
              helperText={error}
              sx={{ marginTop: 2 }}
            />
          </Box>

          {/* Reject Actions */}
          <Box sx={{ display: "flex", justifyContent: "end" }}>
            <Button
              variant="text"
              onClick={handleRejectClose}
              sx={{
                color: "red",
                marginRight: 2,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="text"
              onClick={handleRejectSubmit}
              sx={{
                color: "green",
                marginRight: 2,
              }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};


export default React.memo(DriverAproveReject);
